import React, { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI, keysAPI, messagesAPI } from './api';

// Context for Admin Auth state
const AuthContext = createContext(null);

// Protected Route for Admin only
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useContext(AuthContext);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-300">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Navbar Component
const Navbar = () => {
  const { isAdmin, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <nav className="bg-dark-200 border-b border-dark-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <span className="text-white font-bold text-xl">FreeVPN</span>
          </Link>

          <div className="flex items-center gap-3">
            <Link to="/tutorials" className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium">
              Guides
            </Link>
            {isAdmin && (
              <>
                <Link to="/admin" className="text-primary-400 hover:text-primary-300 px-3 py-2 rounded-md text-sm font-medium hidden md:block">
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
    setLoading(false);
  };

  const login = (token) => {
    localStorage.setItem('adminToken', token);
    setIsAdmin(true);
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ isAdmin, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Home Page (Handles Key Claiming Automatically)
const HomePage = () => {
  const [myKeys, setMyKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClaiming, setIsClaiming] = useState(false);
  const [keyStats, setKeyStats] = useState({ available: 0, claimed: 0, byProtocol: { vless: 0, outline: 0, amnezia: 0, total: 0 } });
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    fetchKey();
    fetchStats();
    fetchAnnouncement();
  }, []);

  const fetchAnnouncement = async () => {
    try {
      const data = await adminAPI.getAnnouncement();
      setAnnouncement(data.announcement || '');
    } catch (e) {}
  };

  const fetchStats = async () => {
    try {
      const stats = await keysAPI.getStats();
      setKeyStats(stats);
    } catch (error) {
      // silently ignore stats errors
    }
  };

  const fetchKey = async () => {
    try {
      const data = await keysAPI.getMyKey();
      setMyKeys(data.keys || []);
    } catch (error) {
      console.error('Error fetching key:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClaimKey = async () => {
    const protocol_type = selectedProtocol;
    if (!protocol_type) {
      toast.error('Please select a protocol first!');
      return;
    }
    setIsClaiming(true);
    try {
      const data = await keysAPI.claimKey(protocol_type);
      setMyKeys(prev => [...prev, data.key]);
      fetchStats(); // Fresh stats after claim
      setSelectedProtocol(null); // Reset selection
      toast.success('VPN key generated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsClaiming(false);
    }
  };

  const copyKey = (keyValue) => {
    if (!keyValue) return;
    
    // Robust copy fallback for all environments
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(keyValue).then(() => {
          toast.success('Key copied successfully! ✅');
        });
      } else {
        // Fallback for non-secure or legacy environments
        const textArea = document.createElement("textarea");
        textArea.value = keyValue;
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Key copied! (Fallback Mode) ✅');
      }
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Failed to copy. Please copy manually.');
    }
  };

  return (
    <div className="min-h-screen bg-dark-300">
      {/* Announcement Banner */}
      {announcement && (
        <div className="bg-yellow-500/20 border-b border-yellow-500/40 px-4 py-3 text-center">
          <p className="text-yellow-300 text-sm font-medium">📢 {announcement}</p>
        </div>
      )}
      <div className="relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/20 to-dark-300"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-20 pb-10 md:pb-16">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-6xl font-bold text-white mb-4">
              Free Secure{' '}
              <span className="text-primary-500">VPN Keys</span>
            </h1>
            <p className="text-base md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Get one free key per protocol — VLESS, Outline, and Amnezia.
            </p>

            {/* Stats */}
            <div className="flex justify-center gap-4 md:gap-6 mb-12">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse"></div>
                <div>
                  <p className="text-green-400 text-xl font-bold font-mono">{keyStats.available}</p>
                  <p className="text-green-300/40 text-[9px] uppercase tracking-tighter">Available</p>
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-400"></div>
                <div>
                  <p className="text-blue-400 text-xl font-bold font-mono">{keyStats.claimed}</p>
                  <p className="text-blue-300/40 text-[9px] uppercase tracking-tighter">Claimed</p>
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto">
              {isLoading ? (
                <div className="py-20 flex flex-col items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary-500 mb-4"></div>
                  <p className="text-gray-500 text-xs">Syncing Keys...</p>
                </div>
              ) : (
                <div className="space-y-10">
                  {/* Active Keys Section - REDESIGNED DASHBOARD */}
                  {myKeys.length > 0 && (
                    <div className="text-left animate-in fade-in duration-500">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-black text-white flex items-center gap-3">
                          <span className="w-2 h-8 bg-primary-500 rounded-full shadow-lg shadow-primary-500/40"></span>
                          My VPN Keys 🔑
                        </h2>
                        <span className="bg-dark-100 text-gray-400 text-[10px] px-3 py-1 rounded-full border border-dark-50 font-bold uppercase tracking-widest">
                          {myKeys.length} / 3 Claimed
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-5">
                        {myKeys.map((keyData) => (
                          <div key={keyData.id} className="bg-dark-200/80 backdrop-blur-sm border-2 border-primary-500/20 rounded-2xl p-6 shadow-2xl relative overflow-hidden transition-all hover:border-primary-500/50 hover:bg-dark-100 group">
                            {/* Decorative Protocol Icon Watermark */}
                            <div className="absolute -right-4 -bottom-4 w-32 h-32 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity grayscale-0">
                               <img src={`/icons/${keyData.protocol_type}.png`} alt="" className="w-full h-full object-contain" />
                            </div>

                            <div className="flex flex-col gap-5 relative z-10">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-14 h-14 rounded-2xl bg-dark-300 border border-dark-100 flex items-center justify-center p-2 shadow-xl overflow-hidden">
                                    <img src={`/icons/${keyData.protocol_type}.png`} alt={keyData.protocol_type} className="w-full h-full object-contain" />
                                  </div>
                                  <div>
                                    <h3 className="text-white text-lg font-black uppercase tracking-tight">{keyData.protocol_type} Premium</h3>
                                    <div className="flex items-center gap-3 mt-1">
                                      <span className="bg-primary-500/20 text-primary-400 text-[9px] font-black px-2 py-0.5 rounded border border-primary-500/30 shadow-sm">ID: {keyData.key_id || '---'}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                                        <span className="text-green-400 text-[10px] font-bold uppercase tracking-wider">Active</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <Link to={`/tutorials/${keyData.protocol_type}`} className="bg-primary-500/10 hover:bg-primary-500/20 text-primary-400 hover:text-primary-300 p-3 rounded-xl border border-primary-500/30 transition-all flex items-center gap-2 group/btn" title="How to Setup?">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                  <span className="text-[10px] font-bold uppercase tracking-wider hidden md:inline">Setup Guide</span>
                                </Link>
                              </div>

                              <div className="space-y-2">
                                <label className="text-gray-500 text-[10px] uppercase font-black tracking-[0.2em] ml-1">Universal Key Address</label>
                                <div className="flex gap-2">
                                  <div className="flex-1 bg-dark-300 border border-dark-100 rounded-xl px-4 py-4 font-mono text-xs text-primary-300 break-all leading-relaxed shadow-inner flex items-center justify-between">
                                    <span className="truncate max-w-[180px] md:max-w-none">
                                      {keyData.key_value.length > 25 
                                        ? `${keyData.key_value.substring(0, 12)}...${keyData.key_value.substring(keyData.key_value.length - 8)}` 
                                        : keyData.key_value}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => copyKey(keyData.key_value)}
                                    className="bg-primary-600 hover:bg-primary-500 text-white px-6 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-90 shadow-lg shadow-primary-900/40 flex flex-col items-center justify-center gap-1 group"
                                  >
                                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                                    <span>COPY</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Claim New Key Section */}
                  {myKeys.length < 3 && (
                    <div className="text-left">
                      {/* Quick Start Guide - Burmese */}
                      <div className="bg-dark-200/50 border border-dark-100 rounded-2xl p-6 mb-10">
                        <h2 className="text-white font-bold mb-6 flex items-center gap-2 text-sm uppercase tracking-widest text-primary-400">
                          <span className="w-1.5 h-4 bg-primary-500 rounded-full"></span>
                          အသုံးပြုနည်း လမ်းညွှန် (Quick Guide)
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
                          {[
                            { step: '၁', title: 'Protocol ရွေးပါ', desc: 'VLESS, Outline သို့မဟုတ် Amnezia ထဲမှ ကြိုက်နှစ်သက်ရာ တစ်ခုကို အရင်ရွေးချယ်ပါ။' },
                            { step: '၂', title: 'Key ထုတ်ယူပါ', desc: '"Generate Key" ခလုတ်ကို နှိပ်ပြီး မိမိအတွက် သီးသန့် Key ကို အခမဲ့ ထုတ်ယူပါ။' },
                            { step: '၃', title: 'Copy ကူးပါ', desc: 'ရလာတဲ့ Key ကို "COPY" ခလုတ်နှိပ်ပြီး ကူးယူပါ။ (Compact UI ကြောင့် Key ကို အတိုချုံ့ပြထားပါမည်)' },
                            { step: '၄', title: 'အသုံးပြုပုံဖတ်ပါ', desc: 'Key card ဘေးရှိ "Setup Guide" (i) icon ကိုနှိပ်ပြီး App ထဲတွင် ထည့်သွင်းနည်းကို ဖတ်ပါ။' },
                          ].map((item, idx) => (
                            <div key={idx} className="relative p-4 bg-dark-300/50 rounded-xl border border-dark-100 group hover:border-primary-500/30 transition-all">
                              <span className="absolute -top-3 -left-2 w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-black text-xs shadow-lg ring-4 ring-dark-300">{item.step}</span>
                              <h4 className="text-white font-bold text-xs mt-2 mb-1 uppercase tracking-wider">{item.title}</h4>
                              <p className="text-gray-400 text-[10px] leading-relaxed">{item.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <h2 className="text-white font-bold mb-4 flex items-center gap-2 text-sm uppercase tracking-widest">
                        <span className="w-1.5 h-4 bg-green-500 rounded-full"></span>
                        Claim New Key
                      </h2>
                      <div className="bg-dark-200 border border-dark-100 rounded-2xl p-6 shadow-2xl">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                          {[
                            { id: 'vless', label: 'VLESS', emoji: '🔵' },
                            { id: 'outline', label: 'Outline', emoji: '🟢' },
                            { id: 'amnezia', label: 'Amnezia', emoji: '🟣' },
                          ].map((p) => {
                            const count = keyStats.byProtocol?.[p.id] || 0;
                            const isSelected = selectedProtocol === p.id;
                            const alreadyClaimed = myKeys.some(k => k.protocol_type === p.id);
                            if (alreadyClaimed) return null;
                            return (
                              <button
                                key={p.id}
                                onClick={() => setSelectedProtocol(p.id)}
                                disabled={isClaiming || count === 0}
                                className={`flex flex-col items-center p-4 border rounded-xl transition-all ${isSelected ? 'bg-primary-600/10 border-primary-500 ring-1 ring-primary-500' : 'bg-dark-300 border-dark-100 hover:border-dark-50'} ${count === 0 ? 'opacity-30' : ''}`}
                              >
                                <div className={`w-12 h-12 mb-3 rounded-xl flex items-center justify-center p-2 transition-all ${isSelected ? 'bg-primary-500/20 shadow-[0_0_15px_rgba(var(--primary-rgb),0.3)]' : 'bg-dark-200'}`}>
                                  <img src={`/icons/${p.id}.png`} alt={p.label} className="w-full h-full object-contain" />
                                </div>
                                <p className="text-white font-bold text-[10px] uppercase tracking-wider">{p.label}</p>
                                <p className="text-[8px] text-gray-500 uppercase mt-0.5">{count} Left</p>
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={handleClaimKey}
                          disabled={isClaiming || !selectedProtocol}
                          className="w-full bg-primary-600 hover:bg-primary-500 disabled:bg-gray-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {isClaiming && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                          {isClaiming ? 'Generating...' : `Generate ${selectedProtocol?.toUpperCase() || ''} Key`}
                        </button>
                      </div>
                    </div>
                  )}

                  {myKeys.length === 0 && !isLoading && !selectedProtocol && (
                    <p className="text-gray-600 text-xs">Select a protocol to get your free key.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Admin Box */}
      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="bg-dark-200 border border-dark-100 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-1">💬 Admin ကို စာပို့ပါ</h3>
          <p className="text-gray-500 text-xs mb-3">Key ပြဿနာ၊ မေးခွန်း၊ Report တွေကို Admin ဆီ တိုက်ရိုက်ပို့ နိုင်ပါတယ်</p>
          <ContactForm />
        </div>
      </div>

      {/* Hidden Admin Link in Footer */}
      <footer className="py-6 text-center border-t border-dark-100">
        <Link to="/admin/login" className="text-dark-100 text-xs hover:text-gray-500 transition-colors">
          Admin Portal
        </Link>
      </footer>
    </div>
  );
};

// Reusable Contact Form component
const ContactForm = () => {
  const [msgText, setMsgText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!msgText.trim()) return;
    setIsSending(true);
    try {
      await messagesAPI.send(msgText);
      toast.success('Message sent to admin!');
      setMsgText('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <form onSubmit={handleSend}>
      <textarea
        value={msgText}
        onChange={(e) => setMsgText(e.target.value)}
        placeholder="သင့်မေးခွန်း သို့မဟုတ် ပြဿနာ ရေးပါ..."
        maxLength={1000}
        rows={3}
        className="w-full px-3 py-2 bg-dark-300 border border-dark-100 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 resize-none mb-2"
      />
      <div className="flex items-center justify-between">
        <span className="text-gray-600 text-xs">{msgText.length}/1000</span>
        <button
          type="submit"
          disabled={isSending || !msgText.trim()}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {isSending ? 'Sending...' : 'Send Message'}
        </button>
      </div>
    </form>
  );
};


// Admin Login Page
const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { token } = await adminAPI.login(email, password);
      login(token);
      toast.success('Admin login successful!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-300 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white">Admin Access</h1>
          <p className="text-gray-400 mt-2">Sign in to manage VPN keys</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-dark-200 border border-dark-100 rounded-xl p-8 shadow-2xl">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white focus:outline-none focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white focus:outline-none focus:border-primary-500"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Authenticating...' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Tutorials Page
const TutorialsPage = () => {
  const [tutorials, setTutorials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const { tutorialsAPI } = await import('./api');
      const data = await tutorialsAPI.getAll();
      setTutorials(data.tutorials);
    } catch (error) {
      console.error('Error fetching tutorials:', error);
    } finally {
      setIsLoading(false);
    }
  };


  const staticProtocols = [
    { protocol_type: 'vless', title: 'VLESS VPN', desc: 'High-performance modern protocol' },
    { protocol_type: 'outline', title: 'Outline VPN', desc: 'Easy Shadowsocks-based VPN' },
    { protocol_type: 'amnezia', title: 'AmneziaVPN', desc: 'Censorship-resistant protocol' },
  ];

  // Merge DB tutorials with static fallback - always show all 3
  const displayList = staticProtocols.map(sp => {
    const dbTutorial = tutorials?.find(t => t.protocol_type === sp.protocol_type);
    return dbTutorial || sp;
  });

  return (
    <div className="min-h-screen bg-dark-300">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-white mb-2">VPN Setup Guides</h1>
        <p className="text-gray-400 mb-8">Step-by-step guide to download the app and import your key</p>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {displayList.map((item) => (
              <Link
                key={item.protocol_type}
                to={`/tutorials/${item.protocol_type}`}
                className="bg-dark-200 border border-dark-100 rounded-xl p-6 hover:border-primary-500 transition-colors group"
              >
                <div className="w-16 h-16 mb-4 bg-dark-300 rounded-2xl flex items-center justify-center p-3 border border-dark-100 shadow-xl group-hover:border-primary-500/50 transition-colors">
                  <img src={`/icons/${item.protocol_type}.png`} alt={item.protocol_type} className="w-full h-full object-contain" />
                </div>
                <h2 className="text-xl font-semibold text-white group-hover:text-primary-500 transition-colors">
                  {item.title}
                </h2>
                <p className="text-gray-400 text-sm mt-2">
                  {item.desc || 'Click to view setup instructions'}
                </p>
                <p className="text-primary-500 text-xs mt-3 font-medium">View Guide →</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Tutorial Detail Page
const TutorialDetailPage = () => {
  const [tutorial, setTutorial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const protocol = location.pathname.split('/').pop() || '';

  useEffect(() => {
    const fetchTutorial = async () => {
      try {
        const { tutorialsAPI } = await import('./api');
        const data = await tutorialsAPI.getByProtocol(protocol);
        setTutorial(data.tutorial);
      } catch (error) {
        console.error('Error fetching tutorial:', error);
      } finally {
        setIsLoading(false);
      }
    };
    if (protocol) fetchTutorial();
  }, [protocol]);

  const renderContent = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-2xl font-bold text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-xl font-semibold text-white mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('- ')) {
        return <li key={index} className="text-gray-300 ml-4 list-disc mb-1">{line.replace('- ', '')}</li>;
      }
      if (line.match(/^\d+\./)) {
        return <li key={index} className="text-gray-300 ml-4 list-decimal mb-1">{line.replace(/^\d+\./, '').trim()}</li>;
      }
      if (line.trim() === '') {
        return <div key={index} className="h-4" />;
      }
      return <p key={index} className="text-gray-300 mb-4 leading-relaxed">{line}</p>;
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-300 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Built-in static guides (fallback)
  const staticGuides = {
    vless: {
      title: 'VLESS VPN Setup Guide',
      apps: [
        { name: 'v2rayNG', platform: 'Android', url: 'https://play.google.com/store/apps/details?id=com.v2ray.ang' },
        { name: 'Streisand', platform: 'iOS', url: 'https://apps.apple.com/app/streisand/id6450534064' },
      ],
      steps: [
        'v2rayNG App ကို Download လုပ်ပြီး ဖွင့်ပါ',
        'Homepage မှ VLESS Key ကို Copy ကူးပါ',
        'App ထဲရှိ (+) ကိုနှိပ်ပြီး "Import config from Clipboard" ကိုရွေးပါ',
        'ချိတ်ဆက်ရန် Start (▶) ကို နှိပ်ပါ'
      ]
    },
    outline: {
      title: 'Outline VPN Setup Guide',
      apps: [
        { name: 'Outline Client', platform: 'Android/iOS/PC', url: 'https://getoutline.org/get-started/#step-3' },
      ],
      steps: [
        'Outline App ကို Download လုပ်ပြီး ဖွင့်ပါ',
        'Homepage မှ Outline Key ကို Copy ကူးပါ',
        '"Add Server" ကိုနှိပ်ပြီး Key ကို Paste လုပ်ပါ',
        'Connect ကိုနှိပ်ပြီး အသုံးပြုပါ'
      ]
    },
    amnezia: {
      title: 'AmneziaVPN Setup Guide',
      apps: [
        { name: 'AmneziaVPN', platform: 'All Devices', url: 'https://amnezia.org/en/downloads' },
      ],
      steps: [
        'AmneziaVPN App ကို ဖွင့်ပါ',
        'Homepage မှ Amnezia Key ကို Copy ကူးပါ',
        '"Add server" -> "Add from text" တွင် Paste လုပ်ပါ',
        'Connect နှိပ်ပြီး အသုံးပြုပါ'
      ]
    }
  };

  const guide = tutorial || staticGuides[protocol];

  if (!guide) {
    return (
      <div className="min-h-screen bg-dark-300 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Tutorial Not Found</h1>
          <Link to="/tutorials" className="text-primary-500 hover:underline">Back to Tutorials</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-300">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/tutorials" className="inline-flex items-center text-gray-400 hover:text-white mb-10 group">
          <svg className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Tutorials
        </Link>

        <div className="flex items-center gap-6 mb-12">
          <div className="w-20 h-20 bg-dark-200 border border-dark-100 rounded-3xl flex items-center justify-center p-4 shadow-2xl relative">
            <div className="absolute inset-0 bg-primary-500/10 blur-2xl rounded-full"></div>
            <img src={`/icons/${protocol}.png`} alt={protocol} className="w-full h-full object-contain relative z-10" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
            {guide.title}
          </h1>
        </div>

        {/* App Downloads */}
        {guide.apps && (
          <div className="bg-dark-200 border border-dark-100 rounded-2xl p-6 mb-8 shadow-xl">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span className="w-1 h-4 bg-primary-500 rounded-full"></span>
              📱 App ဒေါင်းရန်
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {guide.apps.map((app, i) => (
                <a key={i} href={app.url} target="_blank" rel="noopener noreferrer" 
                   className="flex items-center justify-between bg-dark-300 hover:bg-primary-600/10 border border-dark-100 hover:border-primary-500 rounded-xl px-4 py-4 transition-all group/link">
                  <div>
                    <p className="text-white font-bold text-sm tracking-tight">{app.name}</p>
                    <p className="text-gray-500 text-[10px] uppercase font-black tracking-widest mt-0.5">{app.platform}</p>
                  </div>
                  <svg className="w-5 h-5 text-primary-500 opacity-0 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-dark-200 border border-dark-100 rounded-2xl p-8 mb-10 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] grayscale">
             <img src={`/icons/${protocol}.png`} alt="" className="w-32 h-32 object-contain" />
          </div>
          <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2 relative z-10">
            <span className="w-1 h-4 bg-primary-500 rounded-full"></span>
            🔑 Key ထည့်နည်း
          </h2>
          <div className="relative z-10">
            {tutorial ? renderContent(tutorial.instructions) : (
              <ol className="space-y-4">
                {guide.steps.map((step, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <span className="w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-black flex items-center justify-center shrink-0 mt-0.5 shadow-lg shadow-primary-900/40">{i + 1}</span>
                    <p className="text-gray-300 leading-relaxed text-sm pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div className="text-center">
          <Link to="/" className="text-primary-500 hover:text-white font-bold text-sm uppercase tracking-widest transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [unreadMSgs, setUnreadMsgs] = useState(0);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsData, msgData] = await Promise.all([
        adminAPI.getStats(),
        messagesAPI.getAll()
      ]);
      setStats(statsData.stats);
      setUnreadMsgs(msgData.messages?.filter(m => !m.is_read).length || 0);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-300 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-300">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-dark-100">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-primary-500 border-b-2 border-primary-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Overview
          </button>
        </div>

        {activeTab === 'overview' && stats && (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-dark-200 border border-dark-100 rounded-xl p-6">
              <p className="text-gray-400 text-sm mb-2">Total Keys</p>
              <p className="text-3xl font-bold text-white">{stats.totalKeys}</p>
            </div>
            <div className="bg-dark-200 border border-dark-100 rounded-xl p-6">
              <p className="text-gray-400 text-sm mb-2">Available Keys</p>
              <p className="text-3xl font-bold text-green-500">{stats.unusedKeys}</p>
            </div>
            <div className="bg-dark-200 border border-dark-100 rounded-xl p-6">
              <p className="text-gray-400 text-sm mb-2">Claimed Keys</p>
              <p className="text-3xl font-bold text-purple-500">{stats.assignedKeys}</p>
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-4">
          <Link
            to="/admin/keys"
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg transition-colors inline-block font-semibold"
          >
            Manage VPN Keys
          </Link>
          <Link
            to="/admin/messages"
            className="relative bg-dark-200 hover:bg-dark-100 text-white px-6 py-3 rounded-lg transition-colors inline-block font-semibold border border-dark-100"
          >
            📬 User Messages
            {unreadMSgs > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-dark-300 animate-bounce">
                {unreadMSgs}
              </span>
            )}
          </Link>
          <Link
            to="/admin/settings"
            className="border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white px-6 py-3 rounded-lg transition-colors inline-block font-semibold"
          >
            ⚙️ Settings
          </Link>
        </div>
      </div>
    </div>
  );
};

// Admin Keys Page
const AdminKeysPage = () => {
  const [keys, setKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newKey, setNewKey] = useState({ protocol_type: 'vless', key_value: '' });
   const [isAdding, setIsAdding] = useState(false);
   const [filterProtocol, setFilterProtocol] = useState('all');

   // Natural sorting function for Key IDs (e.g., VL-001 < VL-010)
   const sortKeys = (keysToSort) => {
     return [...keysToSort].sort((a, b) => {
       if (!a.key_id || !b.key_id) return 0;
       const numA = parseInt(a.key_id.split('-')[1]) || 0;
       const numB = parseInt(b.key_id.split('-')[1]) || 0;
       if (a.protocol_type !== b.protocol_type) {
         return a.protocol_type.localeCompare(b.protocol_type);
       }
       return numA - numB;
     });
   };

   const filteredKeys = sortKeys(
     filterProtocol === 'all' 
       ? keys 
       : keys.filter(k => k.protocol_type === filterProtocol)
   );

   const getCount = (protocol) => {
     if (protocol === 'all') return keys.length;
     return keys.filter(k => k.protocol_type === protocol).length;
   };

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const data = await adminAPI.getKeys();
      setKeys(data.keys);
    } catch (error) {
      console.error('Error fetching keys:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddKey = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      await adminAPI.addKey(newKey.protocol_type, newKey.key_value);
      toast.success('Key added successfully!');
      setShowAddModal(false);
      setNewKey({ protocol_type: 'vless', key_value: '' });
      fetchKeys();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleResetKey = async (id) => {
    if (!window.confirm('Reset this key to unused? The user who claimed it will lose access.')) return;
    try {
      await adminAPI.resetKey(id);
      toast.success('Key reset to unused!');
      fetchKeys();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteKey = async (id) => {
    if (!window.confirm('Are you sure you want to delete this key?')) return;

    try {
      await adminAPI.deleteKey(id);
      toast.success('Key deleted successfully!');
      fetchKeys();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-dark-300">
      <div className="max-w-7xl mx-auto px-4 py-8">
         <Link to="/admin" className="text-gray-400 hover:text-white mb-6 inline-block">&larr; Back to Dashboard</Link>
        <div className="flex justify-between items-center mb-8 mt-2">
          <h1 className="text-3xl font-bold text-white">Manage VPN Keys</h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add New Key
          </button>
        </div>

        {/* Protocol Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 p-1 bg-dark-200 border border-dark-100 rounded-xl w-fit">
          {['all', 'vless', 'outline', 'amnezia'].map((p) => (
            <button
              key={p}
              onClick={() => setFilterProtocol(p)}
              className={`px-6 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                filterProtocol === p 
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-900/40' 
                  : 'text-gray-500 hover:text-gray-300 hover:bg-dark-100'
              }`}
            >
              {p}
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                filterProtocol === p ? 'bg-primary-700 text-primary-200' : 'bg-dark-300 text-gray-600'
              }`}>
                {getCount(p)}
              </span>
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <div className="bg-dark-200 border border-dark-100 rounded-xl overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-300">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Protocol</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Key Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Claimed IP</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-100">
                {filteredKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-dark-300/50">
                   <td className="px-6 py-4 whitespace-nowrap text-sm font-black text-primary-400 font-mono">{key.key_id || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white capitalize">{key.protocol_type}</td>
                    <td className="px-6 py-4 text-sm text-gray-300 font-mono max-w-xs truncate" title={key.key_value}>{key.key_value}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        key.status === 'unused' ? 'bg-green-600/20 text-green-400' : 'bg-purple-600/20 text-purple-400'
                      }`}>
                        {key.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {key.user_email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm flex gap-3">
                      {key.status === 'assigned' && (
                        <button
                          onClick={() => handleResetKey(key.id)}
                          className="text-yellow-500 hover:text-yellow-400 font-medium"
                        >
                          Reset
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="text-red-500 hover:text-red-400 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {keys.length === 0 && (
                <div className="text-center py-12 text-gray-500">No keys added yet. Add some keys for users to claim!</div>
            )}
          </div>
        )}

        {/* Add Key Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-dark-200 border border-dark-100 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h2 className="text-2xl font-bold text-white mb-6">Add VPN Key</h2>
              <form onSubmit={handleAddKey}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Protocol
                    {newKey.key_value && (
                      <span className="ml-2 text-xs bg-primary-500/20 text-primary-300 px-2 py-0.5 rounded-full">✓ Auto-detected</span>
                    )}
                  </label>
                  <select
                    value={newKey.protocol_type}
                    onChange={(e) => setNewKey({ ...newKey, protocol_type: e.target.value })}
                    className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  >
                    <option value="vless">VLESS</option>
                    <option value="outline">Outline</option>
                    <option value="amnezia">Amnezia VPN</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Key Value String</label>
                  <textarea
                    value={newKey.key_value}
                    onChange={(e) => {
                      const val = e.target.value;
                      const autoProtocol = val.startsWith('ss://') ? 'outline'
                        : val.startsWith('vless://') ? 'vless'
                        : val.trim() ? 'amnezia'
                        : newKey.protocol_type;
                      setNewKey({ ...newKey, key_value: val, protocol_type: autoProtocol });
                    }}
                    placeholder="vless://... or ss://... or paste your key here"
                    className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-primary-500"
                    rows={4}
                    required
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isAdding}
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    {isAdding ? 'Adding...' : 'Add Key'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 border border-gray-600 hover:border-gray-500 text-gray-300 py-3 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Admin Messages Page
const AdminMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const data = await messagesAPI.getAll();
      setMessages(data.messages || []);
    } catch (e) { toast.error('Failed to load messages'); }
    finally { setIsLoading(false); }
  };

  const handleMarkRead = async (id) => {
    try {
      await messagesAPI.markRead(id);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await messagesAPI.delete(id);
      setMessages(prev => prev.filter(m => m.id !== id));
      toast.success('Message deleted');
    } catch (e) { toast.error(e.message); }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="min-h-screen bg-dark-300">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/admin" className="text-gray-400 hover:text-white mb-6 inline-block">&larr; Back to Dashboard</Link>
        <div className="flex items-center gap-3 mb-6 mt-2">
          <h1 className="text-3xl font-bold text-white">📬 User Messages</h1>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">{unreadCount} new</span>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16 text-gray-500">No messages yet</div>
        ) : (
          <div className="space-y-4">
            {messages.map(msg => (
              <div key={msg.id} className={`bg-dark-200 border rounded-xl p-5 ${msg.is_read ? 'border-dark-100' : 'border-primary-500/50'}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    {!msg.is_read && <span className="w-2 h-2 bg-primary-500 rounded-full shrink-0"></span>}
                    <p className="text-gray-400 text-xs">From: {msg.ip} &nbsp;·&nbsp; {new Date(msg.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!msg.is_read && (
                      <button onClick={() => handleMarkRead(msg.id)} className="text-xs text-green-400 hover:text-green-300">Mark Read</button>
                    )}
                    <button onClick={() => handleDelete(msg.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                  </div>
                </div>
                <p className="text-white text-sm whitespace-pre-wrap">{msg.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// Admin Settings Page
const AdminSettingsPage = () => {
  const [announcement, setAnnouncement] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPw, setIsChangingPw] = useState(false);

  useEffect(() => {
    adminAPI.getAnnouncement().then(d => setAnnouncement(d.announcement || '')).catch(() => {});
  }, []);

  const handleSaveAnnouncement = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await adminAPI.saveAnnouncement(announcement);
      toast.success('Announcement updated!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return toast.error('New passwords do not match');
    setIsChangingPw(true);
    try {
      await adminAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsChangingPw(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-300">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/admin" className="text-gray-400 hover:text-white mb-6 inline-block">&larr; Back to Dashboard</Link>
        <h1 className="text-3xl font-bold text-white mb-8 mt-2">Admin Settings</h1>

        {/* Announcement */}
        <div className="bg-dark-200 border border-dark-100 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">📢 Site Announcement</h2>
          <p className="text-gray-400 text-sm mb-4">This message will appear as a banner to all users on the homepage. Leave empty to hide the banner.</p>
          <form onSubmit={handleSaveAnnouncement}>
            <textarea
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              placeholder="Type your announcement here... (leave empty to clear)"
              className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 mb-4"
              rows={3}
            />
            <button
              type="submit"
              disabled={isSaving}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Announcement'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-dark-200 border border-dark-100 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-white mb-4">🔑 Change Admin Password</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Current Password</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required
                className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6}
                className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                className="w-full px-4 py-3 bg-dark-300 border border-dark-100 rounded-lg text-white focus:outline-none focus:border-primary-500" />
            </div>
            <button
              type="submit"
              disabled={isChangingPw}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isChangingPw ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


// Main App Component
function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-dark-300 font-sans text-gray-100">
        <Navbar />

        {/* Floating Telegram Contact Button */}
        <a
          href="https://t.me/truehand_zin"
          target="_blank"
          rel="noopener noreferrer"
          title="Contact Admin on Telegram"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#229ED9] hover:bg-[#1a8bbf] text-white px-4 py-3 rounded-full shadow-lg transition-all hover:scale-105 font-semibold text-sm"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.19 13.982l-2.96-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.958.577z"/>
          </svg>
          Contact Admin
        </a>

        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/tutorials" element={<TutorialsPage />} />
          <Route path="/tutorials/:protocol" element={<TutorialDetailPage />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/keys"
            element={
              <AdminRoute>
                <AdminKeysPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <AdminSettingsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <AdminRoute>
                <AdminMessagesPage />
              </AdminRoute>
            }
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;
